/**
 * AnimatedEqualizer — 5-bar animated equalizer visualization.
 *
 * When isPlaying=true: bars animate up/down independently with staggered
 * delays and durations so they feel organic, not synchronized.
 * When isPlaying=false: bars freeze at a natural resting height with a
 * smooth crossfade transition.
 *
 * Designed to be a drop-in replacement for the static waveform icon in
 * MiniPlayerBar ringtone card rows. Same 28×28 footprint.
 */

import React from 'react';

// Bar configurations: [resting height ratio, animation duration (s), delay (s)]
// Heights oscillate between ~20% and 100% of the container interior height.
const BAR_CONFIG: [number, string, string][] = [
  [0.50, '0.72s', '0s'],
  [0.85, '0.58s', '0.15s'],
  [0.65, '0.81s', '0.30s'],
  [1.00, '0.64s', '0.45s'],
  [0.55, '0.76s', '0.20s'],
];

// Keyframe name injected once
const KEYFRAME_ID = 'poof-eq-bar';

function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAME_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAME_ID;
  style.textContent = `
    @keyframes eqBar {
      0%   { transform: scaleY(0.2); }
      50%  { transform: scaleY(1);   }
      100% { transform: scaleY(0.2); }
    }
  `;
  document.head.appendChild(style);
}

interface AnimatedEqualizerProps {
  isPlaying: boolean;
  /** Container size in px — defaults to 28 to match MiniPlayerBar's waveform circle */
  size?: number;
  /** Accent colour for the bars — defaults to the app's cyan accent */
  color?: string;
}

export function AnimatedEqualizer({
  isPlaying,
  size = 28,
  color = 'rgba(0,229,255,1)',
}: AnimatedEqualizerProps) {
  // Inject keyframes on first render
  React.useEffect(() => {
    ensureKeyframes();
  }, []);

  // Interior height available for bars (circle inset)
  const innerH = Math.round(size * 0.5);   // 14px at default size
  const barW = Math.round(size * 0.07);    // ~2px at default
  const gap = Math.round(size * 0.065);    // ~1.8px at default

  // Derive circle tint colors from the passed color string
  // Support both rgba(...) and hex (#rrggbb) formats
  const circleBg = color.startsWith('#')
    ? `${color}1a`
    : color.replace(/[\d.]+\)$/, '0.1)');
  const circleBorder = color.startsWith('#')
    ? `${color}40`
    : color.replace(/[\d.]+\)$/, '0.25)');

  return (
    <div
      style={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        background: circleBg,
        border: `1px solid ${circleBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap,
        transition: 'border-color 0.3s ease, background 0.3s ease',
      }}
    >
      {BAR_CONFIG.map(([restH, dur, delay], i) => {
        const barColor = color.replace(/[\d.]+\)$/, `${0.45 + restH * 0.55})`);
        return (
          <div
            key={i}
            style={{
              width: barW,
              height: innerH,
              borderRadius: barW,
              background: barColor,
              transformOrigin: 'center bottom',
              transition: 'transform 0.35s ease',
              ...(isPlaying
                ? {
                    animation: `eqBar ${dur} ease-in-out ${delay} infinite`,
                  }
                : {
                    transform: `scaleY(${0.22 + restH * 0.28})`,
                  }),
            }}
          />
        );
      })}
    </div>
  );
}

export default AnimatedEqualizer;
